// src/pages/CompanyProfile.js
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  FaTrashAlt,
} from "react-icons/fa";

import Cropper from "react-easy-crop";

import api from "../axiosConfig";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/CompanyProfile.css";

/* ------------------------------ helpers ------------------------------ */

const absoluteFromApiBase = (
  path
) => {
  if (!path) {
    return null;
  }

  if (
    /^https?:\/\//i.test(
      path
    )
  ) {
    return path;
  }

  const base = (
    api.defaults?.baseURL ||
    ""
  ).replace(
    /\/$/,
    ""
  );

  return base
    ? `${base}${
        path.startsWith("/")
          ? ""
          : "/"
      }${path}`
    : path;
};

const makePreview = (
  value
) => {
  if (!value) {
    return null;
  }

  if (
    value instanceof File
  ) {
    return URL.createObjectURL(
      value
    );
  }

  if (
    typeof value ===
    "string"
  ) {
    return absoluteFromApiBase(
      value
    );
  }

  return null;
};

const normalizeWebsite = (
  raw
) => {
  const value =
    (raw || "").trim();

  if (!value) {
    return "";
  }

  if (
    /^(https?:)?\/\//i.test(
      value
    )
  ) {
    return value.startsWith(
      "//"
    )
      ? `https:${value}`
      : value;
  }

  return `https://${value}`;
};

const EMPTY_COMPANY_PROFILE_FORM = {
  name: "",
  address: "",
  city: "",
  zipCode: "",
  country: "",
  email: "",
  phoneNumber: "",
  ceo: "",
  website: "",
  photo: null,
};

const companyProfileFromRow = (
  row
) => {
  if (!row) {
    return {
      ...EMPTY_COMPANY_PROFILE_FORM,
    };
  }

  return {
    name:
      row.name ||
      row.company_name ||
      "",

    address:
      row.address ||
      "",

    city:
      row.city ||
      "",

    zipCode:
      row.postal_code ||
      row.zipCode ||
      row.postalCode ||
      "",

    country:
      row.country ||
      "",

    email:
      row.companyEmail ||
      row.company_email ||
      row.email ||
      "",

    phoneNumber:
      row.companyPhoneNumber ||
      row.company_phone_number ||
      row.phoneNumber ||
      row.phone_number ||
      row.phone ||
      row.company_phone ||
      "",

    ceo:
      row.ceo ||
      row.CEO ||
      row.main_contact_name ||
      row.owner_name ||
      "",

    website:
      row.website ||
      row.company_website ||
      row.site ||
      row.url ||
      "",

    photo:
      (() => {
        const raw =
          row.photoCompany ||
          row.photo_company ||
          null;

        if (
          !raw
        ) {
          return null;
        }

        /*
         * IMPORTANT:
         *
         * The backend returns a presigned S3/Hetzner URL.
         * Never append query parameters to an already absolute
         * signed URL because that can invalidate its signature.
         */
        if (
          /^https?:\/\//i.test(
            raw
          )
        ) {
          return raw;
        }

        const revision =
          row.updatedAt ||
          row.updated_at ||
          Date.now();

        const separator =
          raw.includes("?")
            ? "&"
            : "?";

        return `${raw}${separator}v=${encodeURIComponent(
          revision
        )}`;
      })(),
  };
};

/* ------------------------------ crop helpers ------------------------------ */

const createImage = (
  url
) =>
  new Promise(
    (
      resolve,
      reject
    ) => {
      const image =
        new Image();

      image.onload =
        () =>
          resolve(
            image
          );

      image.onerror =
        reject;

      image.crossOrigin =
        "anonymous";

      image.src =
        url;
    }
  );

const getCroppedBlob =
  async (
    imageSrc,
    pixelCrop
  ) => {
    const image =
      await createImage(
        imageSrc
      );

    const canvas =
      document.createElement(
        "canvas"
      );

    const context =
      canvas.getContext(
        "2d"
      );

    if (!context) {
      throw new Error(
        "Canvas context not available"
      );
    }

    const width =
      Math.max(
        1,
        Math.round(
          pixelCrop.width
        )
      );

    const height =
      Math.max(
        1,
        Math.round(
          pixelCrop.height
        )
      );

    canvas.width =
      width;

    canvas.height =
      height;

    context.clearRect(
      0,
      0,
      width,
      height
    );

    const x =
      Math.round(
        pixelCrop.x
      );

    const y =
      Math.round(
        pixelCrop.y
      );

    context.drawImage(
      image,
      -x,
      -y
    );

    const blob =
      await new Promise(
        (
          resolve
        ) =>
          canvas.toBlob(
            (
              result
            ) =>
              resolve(
                result
              ),
            "image/png"
          )
      );

    if (!blob) {
      throw new Error(
        "Failed to create image blob"
      );
    }

    return blob;
  };

const CompanyProfile = forwardRef(function CompanyProfile(
  {
    embedded = false,
    onSaved,
  },
  ref
) {
  const { t } =
    useTranslation(
      "company_profile"
    );

  const {
    data,
    refresh,
    patchSection,
  } =
    useCompanySettingsData();

  const details =
    data?.details ||
    null;

  const dirtyRef =
    useRef(false);

  const [
    form,
    setForm,
  ] = useState(
    () =>
      companyProfileFromRow(
        details
      )
  );

  const [
    detailsId,
    setDetailsId,
  ] = useState(
    details?.id ??
      null
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    savingPhoto,
    setSavingPhoto,
  ] = useState(false);

  /* ------------------------------ crop state ------------------------------ */

  const [
    isCropOpen,
    setIsCropOpen,
  ] = useState(false);

  const [
    cropSrc,
    setCropSrc,
  ] = useState(null);

  const [
    crop,
    setCrop,
  ] = useState({
    x: 0,
    y: 0,
  });

  const [
    zoom,
    setZoom,
  ] = useState(1);

  const croppedAreaPixelsRef =
    useRef(null);

  const cropSrcOwnedRef =
    useRef(false);

  const fileInputRef =
    useRef(null);

  const onCropComplete =
    useCallback(
      (
        _,
        croppedPixels
      ) => {
        croppedAreaPixelsRef.current =
          croppedPixels;
      },
      []
    );

  /* ------------------------------ sync cache ------------------------------ */

  useEffect(() => {
    if (
      !details ||
      dirtyRef.current
    ) {
      return;
    }

    setDetailsId(
      details.id ??
        null
    );

    setForm(
      companyProfileFromRow(
        details
      )
    );
  }, [
    details,
  ]);

  /* ------------------------------ preview ------------------------------ */

  const photoPreview =
    useMemo(
      () =>
        makePreview(
          form.photo
        ),
      [
        form.photo,
      ]
    );

  useEffect(() => {
    let blobUrl =
      null;

    if (
      typeof photoPreview ===
        "string" &&
      photoPreview.startsWith(
        "blob:"
      )
    ) {
      blobUrl =
        photoPreview;
    }

    return () => {
      if (!blobUrl) {
        return;
      }

      try {
        URL.revokeObjectURL(
          blobUrl
        );
      } catch {
        // ignore
      }
    };
  }, [
    photoPreview,
  ]);

  const setField = (
    key,
    value
  ) => {
    dirtyRef.current =
      true;

    setForm(
      (
        previous
      ) => ({
        ...previous,
        [key]:
          value,
      })
    );
  };

  const handlePickPhoto =
    useCallback(
      () => {
        if (
          !fileInputRef.current
        ) {
          return;
        }

        fileInputRef.current.value =
          "";

        fileInputRef.current.click();
      },
      []
    );

  const openPhotoEditor =
    useCallback(
      () => {
        if (
          !photoPreview
        ) {
          handlePickPhoto();

          return;
        }

        cropSrcOwnedRef.current =
          false;

        setCropSrc(
          photoPreview
        );

        setCrop({
          x: 0,
          y: 0,
        });

        setZoom(
          1
        );

        croppedAreaPixelsRef.current =
          null;

        setIsCropOpen(
          true
        );
      },
      [
        photoPreview,
        handlePickPhoto,
      ]
    );

  useImperativeHandle(
    ref,
    () => ({
      openPhotoEditor,
    }),
    [
      openPhotoEditor,
    ]
  );

  const handlePhotoChange =
    (
      event
    ) => {
      const file =
        event.target.files?.[0] ||
        null;

      if (!file) {
        return;
      }

      if (
        cropSrcOwnedRef.current &&
        typeof cropSrc ===
          "string" &&
        cropSrc.startsWith(
          "blob:"
        )
      ) {
        try {
          URL.revokeObjectURL(
            cropSrc
          );
        } catch {
          // ignore
        }
      }

      const url =
        URL.createObjectURL(
          file
        );

      cropSrcOwnedRef.current =
        true;

      setCropSrc(
        url
      );

      setCrop({
        x: 0,
        y: 0,
      });

      setZoom(
        1
      );

      croppedAreaPixelsRef.current =
        null;

      setIsCropOpen(
        true
      );
    };

  const removePhoto =
    () =>
      setField(
        "photo",
        null
      );

  const closeCrop =
    () => {
      setIsCropOpen(
        false
      );

      if (
        cropSrcOwnedRef.current &&
        typeof cropSrc ===
          "string" &&
        cropSrc.startsWith(
          "blob:"
        )
      ) {
        try {
          URL.revokeObjectURL(
            cropSrc
          );
        } catch {
          // ignore
        }
      }

      cropSrcOwnedRef.current =
        false;

      setCropSrc(
        null
      );

      croppedAreaPixelsRef.current =
        null;
    };

  const applyCrop =
    async () => {
      if (
        savingPhoto
      ) {
        return;
      }

      try {
        if (
          !cropSrc
        ) {
          return;
        }

        await new Promise(
          (
            resolve
          ) =>
            requestAnimationFrame(
              resolve
            )
        );

        const pixels =
          croppedAreaPixelsRef.current;

        if (
          !pixels
        ) {
          return;
        }

        const blob =
          await getCroppedBlob(
            cropSrc,
            pixels
          );

        if (
          !blob ||
          blob.size < 50
        ) {
          console.warn(
            "[CompanyProfile] Cropped image blob is too small/empty"
          );

          return;
        }

        const file =
          new File(
            [
              blob,
            ],
            `company-photo-${Date.now()}.png`,
            {
              type:
                "image/png",
            }
          );

        const wasDirty =
          dirtyRef.current;

        setForm(
          (
            previous
          ) => ({
            ...previous,
            photo:
              file,
          })
        );

        if (
          !detailsId
        ) {
          dirtyRef.current =
            true;

          closeCrop();

          return;
        }

        setSavingPhoto(
          true
        );

        const formData =
          new FormData();

        formData.append(
          "photoCompany",
          file
        );

        const {
          data:
            freshDetails,
        } =
          await api.put(
            `/company-details/${detailsId}`,
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        const nextPhoto =
          companyProfileFromRow(
            freshDetails
          ).photo;

        setForm(
          (
            previous
          ) => ({
            ...previous,
            photo:
              nextPhoto,
          })
        );

        patchSection(
          "details",
          freshDetails
        );

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved(
            freshDetails,
            {
              skipRefresh:
                true,
            }
          );
        }

        dirtyRef.current =
          wasDirty;

        closeCrop();
      } catch (error) {
        console.error(
          "[CompanyProfile] photo save failed",
          error
        );

        alert(
          t(
            "toasts.saved_error"
          )
        );
      } finally {
        setSavingPhoto(
          false
        );
      }
    };

  /* ------------------------------ submit ------------------------------ */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      setSaving(
        true
      );

      try {
        const websiteNorm =
          normalizeWebsite(
            form.website
          );

        let savedDetailsId =
          detailsId;

        let savedDetails =
          null;

        if (
          savedDetailsId
        ) {
          const formData =
            new FormData();

          formData.append(
            "company_name",
            form.name ||
              ""
          );

          formData.append(
            "name",
            form.name ||
              ""
          );

          formData.append(
            "address",
            form.address ||
              ""
          );

          formData.append(
            "city",
            form.city ||
              ""
          );

          formData.append(
            "postal_code",
            form.zipCode ||
              ""
          );

          formData.append(
            "country",
            form.country ||
              ""
          );

          formData.append(
            "companyEmail",
            (
              form.email ||
              ""
            ).trim()
          );

          formData.append(
            "companyPhoneNumber",
            (
              form.phoneNumber ||
              ""
            ).trim()
          );

          formData.append(
            "ceo",
            form.ceo ||
              ""
          );

          formData.append(
            "website",
            websiteNorm ||
              ""
          );

          if (
            form.photo instanceof
            File
          ) {
            formData.append(
              "photoCompany",
              form.photo
            );
          } else if (
            form.photo ===
            null
          ) {
            formData.append(
              "photoCompanyRemove",
              "1"
            );
          }

          const {
            data:
              updatedDetails,
          } =
            await api.put(
              `/company-details/${savedDetailsId}`,
              formData,
              {
                headers: {
                  "Content-Type":
                    "multipart/form-data",
                },
              }
            );

          savedDetails =
            updatedDetails ||
            null;
        } else {
          const payload = {
            company_id:
              undefined,

            name:
              form.name ||
              "",

            company_name:
              form.name ||
              "",

            address:
              form.address ||
              "",

            city:
              form.city ||
              "",

            postal_code:
              form.zipCode ||
              "",

            country:
              form.country ||
              "",

            companyEmail:
              (
                form.email ||
                ""
              ).trim(),

            companyPhoneNumber:
              (
                form.phoneNumber ||
                ""
              ).trim(),

            ceo:
              form.ceo ||
              "",

            website:
              websiteNorm ||
              "",
          };

          const {
            data:
              created,
          } =
            await api.post(
              "/company-details",
              payload
            );

          savedDetails =
            created ||
            null;

          savedDetailsId =
            created?.id ??
            null;

          setDetailsId(
            savedDetailsId
          );

          if (
            savedDetailsId &&
            form.photo instanceof
              File
          ) {
            const formData =
              new FormData();

            formData.append(
              "photoCompany",
              form.photo
            );

            const {
              data:
                createdWithPhoto,
            } =
              await api.put(
                `/company-details/${savedDetailsId}`,
                formData,
                {
                  headers: {
                    "Content-Type":
                      "multipart/form-data",
                  },
                }
              );

            savedDetails =
              createdWithPhoto ||
              savedDetails;
          }
        }

        dirtyRef.current =
          false;

        let freshDetails =
          null;

        /*
         * Best case:
         *
         * Backend returned the saved row.
         *
         * Patch the shared cache immediately. No extra GET is required
         * before the rest of CompanySettings can react.
         */
        if (
          savedDetails
        ) {
          const patchedSnapshot =
            patchSection(
              "details",
              savedDetails
            );

          freshDetails =
            patchedSnapshot
              ?.details ||
            savedDetails;

          if (
            typeof onSaved ===
            "function"
          ) {
            await onSaved(
              freshDetails,
              {
                skipRefresh:
                  true,
              }
            );
          }
        } else {
          /*
           * Defensive fallback for endpoints which return no row.
           */
          const snapshot =
            typeof onSaved ===
            "function"
              ? await onSaved()
              : await refresh();

          freshDetails =
            snapshot?.details ||
            null;
        }

        if (
          freshDetails
        ) {
          setDetailsId(
            freshDetails.id ??
              savedDetailsId ??
              null
          );

          setForm(
            companyProfileFromRow(
              freshDetails
            )
          );
        }

        alert(
          t(
            "toasts.saved_success"
          )
        );
      } catch (error) {
        console.error(
          "[CompanyProfile] save error",
          error
        );

        alert(
          t(
            "toasts.saved_error"
          )
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  return (
    <div
      className={`company-profile ${
        embedded
          ? "company-profile--embedded"
          : ""
      }`}
    >
      <div className="company-profile__header">
        <h1 className="company-profile__title">
          {t(
            "title"
          )}
        </h1>

        <p className="company-profile__subtitle">
          {t(
            "subtitle"
          )}
        </p>
      </div>

      <form
        className="company-profile__form"
        onSubmit={
          handleSubmit
        }
      >
        <div className="company-profile__top">
          <div className="company-profile__photo">
            <div className="company-profile__photoWrap">
              <div
                className={`company-profile__photoCircle ${
                  photoPreview
                    ? "has-photo"
                    : ""
                }`}
                onClick={
                  openPhotoEditor
                }
                role="button"
                tabIndex={0}
                aria-label={t(
                  "photo.aria"
                )}
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                      "Enter" ||
                    event.key ===
                      " "
                  ) {
                    event.preventDefault();

                    openPhotoEditor();
                  }
                }}
              >
                {photoPreview ? (
                  <img
                    className="company-profile__photoImg"
                    crossOrigin="anonymous"
                    src={
                      photoPreview
                    }
                    alt={t(
                      "photo.alt"
                    )}
                  />
                ) : (
                  <span className="company-profile__photoPlaceholder">
                    {t(
                      "photo.add"
                    )}
                  </span>
                )}
              </div>

              {photoPreview && (
                <button
                  type="button"
                  className="company-profile__photoTrash"
                  aria-label={t(
                    "photo.remove"
                  )}
                  title={t(
                    "photo.remove"
                  )}
                  onClick={(
                    event
                  ) => {
                    event.stopPropagation();

                    removePhoto();
                  }}
                >
                  <FaTrashAlt
                    size={
                      14
                    }
                  />
                </button>
              )}
            </div>

            <input
              ref={
                fileInputRef
              }
              className="company-profile__fileInput"
              type="file"
              accept="image/*"
              onChange={
                handlePhotoChange
              }
              hidden
            />
          </div>

          <div className="company-profile__topFields">
            <div className="company-profile__field company-profile__field--name">
              <label className="company-profile__label">
                {t(
                  "fields.name.label"
                )}{" "}
                *
              </label>

              <input
                className="company-profile__input"
                required
                value={
                  form.name
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "name",
                    event.target.value
                  )
                }
                placeholder={t(
                  "fields.name.placeholder"
                )}
              />
            </div>

            <div className="company-profile__field company-profile__field--ceo">
              <label className="company-profile__label">
                {t(
                  "fields.ceo.label"
                )}
              </label>

              <input
                className="company-profile__input"
                value={
                  form.ceo
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "ceo",
                    event.target.value
                  )
                }
                placeholder={t(
                  "fields.ceo.placeholder"
                )}
              />
            </div>

            <div className="company-profile__field company-profile__field--address">
              <label className="company-profile__label">
                {t(
                  "fields.address.label"
                )}{" "}
                *
              </label>

              <input
                className="company-profile__input"
                required
                value={
                  form.address
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "address",
                    event.target.value
                  )
                }
                placeholder={t(
                  "fields.address.placeholder"
                )}
              />
            </div>
          </div>
        </div>

        <div className="company-profile__bottomGrid">
          <div className="company-profile__field company-profile__field--zip">
            <label className="company-profile__label">
              {t(
                "fields.zipCode.label"
              )}{" "}
              *
            </label>

            <input
              className="company-profile__input"
              required
              value={
                form.zipCode
              }
              onChange={(
                event
              ) =>
                setField(
                  "zipCode",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.zipCode.placeholder"
              )}
            />
          </div>

          <div className="company-profile__field company-profile__field--city">
            <label className="company-profile__label">
              {t(
                "fields.city.label"
              )}{" "}
              *
            </label>

            <input
              className="company-profile__input"
              required
              value={
                form.city
              }
              onChange={(
                event
              ) =>
                setField(
                  "city",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.city.placeholder"
              )}
            />
          </div>

          <div className="company-profile__field company-profile__field--country">
            <label className="company-profile__label">
              {t(
                "fields.country.label"
              )}{" "}
              *
            </label>

            <input
              className="company-profile__input"
              required
              value={
                form.country
              }
              onChange={(
                event
              ) =>
                setField(
                  "country",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.country.placeholder"
              )}
            />
          </div>

          <div className="company-profile__field company-profile__field--phone">
            <label className="company-profile__label">
              {t(
                "fields.phoneNumber.label"
              )}
            </label>

            <input
              className="company-profile__input"
              type="tel"
              value={
                form.phoneNumber
              }
              onChange={(
                event
              ) =>
                setField(
                  "phoneNumber",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.phoneNumber.placeholder"
              )}
            />
          </div>

          <div className="company-profile__field company-profile__field--email">
            <label className="company-profile__label">
              {t(
                "fields.email.label"
              )}
            </label>

            <input
              className="company-profile__input"
              type="email"
              value={
                form.email
              }
              onChange={(
                event
              ) =>
                setField(
                  "email",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.email.placeholder"
              )}
            />
          </div>

          <div className="company-profile__field company-profile__field--website">
            <label className="company-profile__label">
              {t(
                "fields.website.label"
              )}
            </label>

            <input
              className="company-profile__input"
              type="text"
              inputMode="url"
              value={
                form.website
              }
              onChange={(
                event
              ) =>
                setField(
                  "website",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.website.placeholder"
              )}
            />
          </div>
        </div>

        <div className="company-profile__actions">
          <button
            type="button"
            className="company-profile__btn company-profile__btn--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {t(
              "actions.cancel"
            )}
          </button>

          <button
            type="submit"
            className="company-profile__btn company-profile__btn--primary"
            disabled={
              saving
            }
          >
            {saving
              ? t(
                  "actions.saving"
                )
              : t(
                  "actions.save"
                )}
          </button>
        </div>

        {isCropOpen && (
          <div
            className="company-profile__cropOverlay"
            role="dialog"
            aria-modal="true"
          >
            <div className="company-profile__cropModal">
              <div className="company-profile__cropHeader">
                <div className="company-profile__cropTitle">
                  {t(
                    "photo.crop_title",
                    {
                      defaultValue:
                        "Ajustar foto da empresa",
                    }
                  )}
                </div>

                <button
                  type="button"
                  className="company-profile__cropClose"
                  onClick={
                    closeCrop
                  }
                  aria-label={t(
                    "actions.cancel"
                  )}
                >
                  ✕
                </button>
              </div>

              <div className="company-profile__cropArea">
                <Cropper
                  image={
                    cropSrc
                  }
                  crop={
                    crop
                  }
                  zoom={
                    zoom
                  }
                  aspect={1}
                  cropShape="rect"
                  showGrid={
                    false
                  }
                  objectFit="contain"
                  restrictPosition={
                    false
                  }
                  onCropChange={
                    setCrop
                  }
                  onZoomChange={
                    setZoom
                  }
                  onCropComplete={
                    onCropComplete
                  }
                />
              </div>

              <div className="company-profile__cropControls">
                <label className="company-profile__cropLabel">
                  {t(
                    "photo.zoom",
                    {
                      defaultValue:
                        "Zoom",
                    }
                  )}
                </label>

                <input
                  className="company-profile__cropSlider"
                  type="range"
                  min={
                    0.2
                  }
                  max={
                    3
                  }
                  step={
                    0.01
                  }
                  value={
                    zoom
                  }
                  onChange={(
                    event
                  ) =>
                    setZoom(
                      Number(
                        event.target.value
                      )
                    )
                  }
                />

                <div className="company-profile__cropActions">
                  <div className="company-profile__cropActionsLeft">
                    {photoPreview && (
                      <button
                        type="button"
                        className="company-profile__cropBtn company-profile__cropBtn--danger"
                        onClick={() => {
                          removePhoto();
                          closeCrop();
                        }}
                      >
                        {t(
                          "photo.remove",
                          {
                            defaultValue:
                              "Remover foto",
                          }
                        )}
                      </button>
                    )}

                    <button
                      type="button"
                      className="company-profile__cropBtn company-profile__cropBtn--secondary"
                      onClick={
                        handlePickPhoto
                      }
                    >
                      {t(
                        "photo.change",
                        {
                          defaultValue:
                            "Alterar foto",
                        }
                      )}
                    </button>
                  </div>

                  <div className="company-profile__cropActionsRight">
                    <button
                      type="button"
                      className="company-profile__cropBtn company-profile__cropBtn--ghost"
                      onClick={
                        closeCrop
                      }
                    >
                      {t(
                        "actions.cancel"
                      )}
                    </button>

                    <button
                      type="button"
                      className="company-profile__cropBtn company-profile__cropBtn--primary"
                      onClick={
                        applyCrop
                      }
                      disabled={
                        savingPhoto
                      }
                    >
                      {savingPhoto
                        ? t(
                            "photo.saving",
                            {
                              defaultValue:
                                "A guardar...",
                            }
                          )
                        : t(
                            "photo.use",
                            {
                              defaultValue:
                                "Usar foto",
                            }
                          )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
});

export default CompanyProfile;